type StorageChangeSource = 'same-tab' | 'cross-tab';
type StorageChangeType = 'set' | 'remove' | 'clear';

interface StorageChangeDetail {
  key: string | null;
  newValue: string | null;
  oldValue: string | null;
  source: StorageChangeSource;
  type: StorageChangeType;
}

const STORAGE_CHANGE_EVENT = 'storage-change';
const storageEvent = new EventTarget();

function parseStorageValue<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return raw as unknown as T;
  }
}

function dispatchStorageChange(detail: StorageChangeDetail) {
  storageEvent.dispatchEvent(
    new CustomEvent<StorageChangeDetail>(STORAGE_CHANGE_EVENT, {
      detail
    })
  );
}

export function getStorage<T>(key: string): T | null {
  return parseStorageValue<T>(localStorage.getItem(key));
}

export function setStorage<T>(key: string, data: T): void {
  const oldValue = localStorage.getItem(key);
  const newValue = typeof data === 'string' ? data : JSON.stringify(data);
  localStorage.setItem(key, newValue);
  dispatchStorageChange({
    key,
    newValue,
    oldValue,
    source: 'same-tab',
    type: 'set'
  });
}

export function removeStorage(key: string): void {
  const oldValue = localStorage.getItem(key);
  localStorage.removeItem(key);
  dispatchStorageChange({
    key,
    newValue: null,
    oldValue,
    source: 'same-tab',
    type: 'remove'
  });
}

export function clearStorage(): void {
  localStorage.clear();
  dispatchStorageChange({
    key: null,
    newValue: null,
    oldValue: null,
    source: 'same-tab',
    type: 'clear'
  });
}

export function watchStorage<T>(
  targetKey: string,
  callback: (newVal: T | null, oldVal: T | null) => void
): () => void {
  let oldVal = getStorage<T>(targetKey);

  const triggerCallback = () => {
    const newVal = getStorage<T>(targetKey);
    callback(newVal, oldVal);
    oldVal = newVal;
  };

  const sameTabHandler = (event: Event) => {
    const customEvent = event as CustomEvent<StorageChangeDetail>;
    const changedKey = customEvent.detail?.key ?? null;
    if (changedKey !== null && changedKey !== targetKey) return;
    triggerCallback();
  };

  const crossTabHandler = (event: StorageEvent) => {
    if (event.key !== null && event.key !== targetKey) return;
    triggerCallback();
  };

  storageEvent.addEventListener(STORAGE_CHANGE_EVENT, sameTabHandler);
  window.addEventListener('storage', crossTabHandler);

  return () => {
    storageEvent.removeEventListener(STORAGE_CHANGE_EVENT, sameTabHandler);
    window.removeEventListener('storage', crossTabHandler);
  };
}
